import { useEffect } from "react";
import { useLocation } from "react-router";

const NAV_OFFSET = 80; // height of the fixed navbar

/**
 * Client-side scroll behavior for the SPA:
 *  - navigating to a new page resets scroll to the top
 *  - navigating to a `#hash` (same page or cross-page) smooth-scrolls to that
 *    section, offset for the fixed navbar.
 *
 * react-router does neither of these on its own. Mounted once in RootLayout.
 */
export default function ScrollManager() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    const scrollToTarget = () => {
      const el = document.getElementById(id);
      if (!el) return false;
      const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
      return true;
    };

    // Try immediately; if the section isn't mounted yet (cross-page nav),
    // retry on the next frame and once more after a short delay.
    if (scrollToTarget()) return;
    const raf = requestAnimationFrame(scrollToTarget);
    const timer = setTimeout(scrollToTarget, 300);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [pathname, hash, key]);

  return null;
}
